import {
  BellOutlined,
  CheckOutlined,
  LoadingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { css } from '@linaria/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Empty, Flex, Popover, Spin, Typography } from 'antd';
import classNames from 'classnames';
import { useState } from 'react';

import { useMarkAllOrderNotificationsRead } from '@lib/api/hooks/orders/use-mark-all-order-notifications-read';
import { useMarkOrderNotificationRead } from '@lib/api/hooks/orders/use-mark-order-notification-read';
import { orderNotificationsOptions } from '@lib/api/options/orders';
import { ROUTE_PATHS } from '@modules/route-paths';

const styles = {
  list: css`
    width: min(360px, calc(100vw - 32px));
    max-height: 420px;
    overflow-y: auto;
  `,
  header: css`
    width: min(360px, calc(100vw - 32px));
    padding-bottom: var(--ant-padding-xs);
    padding-right: var(--ant-padding-sm);
  `,
  item: css`
    padding: var(--ant-padding-sm);
    border-bottom: 1px solid var(--ant-color-border-secondary);
  `,
  unreadButton: css`
    &.ant-btn {
      color: var(--ant-color-primary);
      border-color: var(--ant-color-primary-border);
      background: var(--ant-color-primary-bg);
    }
  `,
  notificationIcon: css`
    font-size: 24px;
  `,
  popoverRootClassName: css`
    .ant-popover-container {
      min-width: 300px;
      padding-right: 0px;
    }
  `,
  badge: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    cursor: pointer;
    .ant-badge-count {
      box-shadow: 0 0 0 1px var(--ant-color-bg-container);
    }
  `,
  icon: css`
    font-size: 24px;
    color: rgba(0, 0, 0, 0.8);
    cursor: pointer;
  `,
};

const NOTIFICATION_ICONS = {
  purchase: ShoppingCartOutlined,
  sale: ShopOutlined,
} as const;

const OrderNotifications = () => {
  const [open, setOpen] = useState(false);
  const {
    data = [],
    isPending,
    isError,
  } = useQuery(orderNotificationsOptions());

  const { markRead, reading } = useMarkOrderNotificationRead();
  const { markAllRead, readingAll } = useMarkAllOrderNotificationsRead();
  const unread = data.filter((item) => !item.readAt).length;

  const onOpenChange = (next: boolean) => setOpen(next);

  const onSelectNotification = (id: string) => {
    setOpen(false);
    markRead(id).catch(() => undefined);
  };

  const handleReadAll = () => {
    markAllRead().catch(() => undefined);
  };

  const content = isPending ? (
    <Spin indicator={<LoadingOutlined spin />} />
  ) : isError ? (
    <Typography.Text>Não foi possível carregar os avisos.</Typography.Text>
  ) : data.length === 0 ? (
    <Empty description="Nenhum aviso de pedido" />
  ) : (
    <Flex className={styles.list} vertical>
      {data.map((item) => {
        const Icon = NOTIFICATION_ICONS[item.perspective];

        return (
          <Link
            className={styles.item}
            key={item.id}
            to={
              item.perspective === 'purchase'
                ? ROUTE_PATHS.PURCHASE_DETAILS
                : ROUTE_PATHS.SALE_DETAILS
            }
            params={{ orderId: item.orderId }}
            onClick={() => onSelectNotification(item.id)}
          >
            <Flex gap={8} align="flex-start">
              <Icon
                aria-label={
                  item.perspective === 'purchase' ? 'Compra' : 'Venda'
                }
                className={styles.notificationIcon}
              />
              <Flex gap={2} vertical>
                <Typography.Text strong={!item.readAt}>
                  {item.subject}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {item.message}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {new Date(item.createdAt).toLocaleString('pt-BR')}
                </Typography.Text>
              </Flex>
            </Flex>
          </Link>
        );
      })}
    </Flex>
  );

  return (
    <Popover
      title={
        <Flex
          className={styles.header}
          align="center"
          justify="space-between"
          gap="small"
        >
          <Typography.Text strong>Notificações</Typography.Text>
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            loading={readingAll}
            disabled={isPending || isError || unread === 0 || reading}
            onClick={handleReadAll}
          >
            Marcar todas como lidas
          </Button>
        </Flex>
      }
      content={content}
      open={open}
      placement="bottom"
      trigger="click"
      onOpenChange={onOpenChange}
      rootClassName={styles.popoverRootClassName}
    >
      <Badge
        className={styles.badge}
        count={unread}
        overflowCount={99}
        size="small"
      >
        <BellOutlined
          aria-label={`${unread} notificações não lidas`}
          className={classNames(styles.icon, {
            [styles.unreadButton]: unread > 0,
          })}
        />
      </Badge>
    </Popover>
  );
};

export default OrderNotifications;
